"use client";

import * as React from "react";
import Image from "next/image";
import { X } from "lucide-react";

import { extractStrapiMediaUrl } from "@/lib/strapi";
import { usePopupStore } from "@/stores/popup";

function readNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function isActiveFlag(attributes: Record<string, unknown> | null | undefined): boolean {
  const raw =
    typeof attributes?.is_active === "boolean"
      ? attributes.is_active
      : typeof attributes?.isActive === "boolean"
        ? attributes.isActive
        : attributes?.active;
  if (typeof raw === "boolean") return raw;
  return true;
}

export function StartupPopup() {
  const { popup } = usePopupStore();
  const attributes = (popup?.attributes ?? null) as Record<string, unknown> | null;
  const popupId = typeof popup?.id === "number" ? popup.id : null;
  const storageKey = popupId ? `romano:popup:seen:${popupId}` : null;
  const [open, setOpen] = React.useState(false);
  const [imageBox, setImageBox] = React.useState<{ width: number; height: number } | null>(null);

  const close = React.useCallback(() => {
    setOpen(false);
    if (typeof window !== "undefined" && storageKey) {
      window.sessionStorage.setItem(storageKey, "1");
    }
  }, [storageKey]);

  React.useEffect(() => {
    if (!storageKey) return;
    if (!isActiveFlag(attributes)) return;
    if (typeof window === "undefined") return;

    const seen = window.sessionStorage.getItem(storageKey);
    if (!seen) setOpen(true);
  }, [attributes, storageKey]);

  React.useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [close, open]);

  const title =
    readNonEmptyString(attributes?.title) ??
    readNonEmptyString(attributes?.heading) ??
    readNonEmptyString(attributes?.name) ??
    "Info";

  const imageUrl =
    extractStrapiMediaUrl(attributes?.image) ??
    extractStrapiMediaUrl(attributes?.cover_image) ??
    extractStrapiMediaUrl(attributes?.media) ??
    null;

  const href =
    readNonEmptyString(attributes?.url) ??
    readNonEmptyString(attributes?.href) ??
    readNonEmptyString(attributes?.link) ??
    readNonEmptyString(attributes?.link_url) ??
    null;

  React.useEffect(() => {
    if (!open || !imageUrl) {
      setImageBox(null);
      return;
    }
    if (typeof window === "undefined") return;

    let cancelled = false;
    let loadedImage: HTMLImageElement | null = null;

    const computeBox = (naturalWidth: number, naturalHeight: number) => {
      const maxWidth = Math.min(window.innerWidth * 0.92, 520);
      const maxHeight = Math.min(window.innerHeight * 0.8, 500);
      const ratio = naturalHeight > 0 ? naturalWidth / naturalHeight : 1;

      let width = maxWidth;
      let height = width / ratio;
      if (height > maxHeight) {
        height = maxHeight;
        width = height * ratio;
      }

      setImageBox({ width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)) });
    };

    const load = () => {
      loadedImage = new window.Image();
      loadedImage.decoding = "async";
      loadedImage.onload = () => {
        if (cancelled || !loadedImage) return;
        computeBox(loadedImage.naturalWidth, loadedImage.naturalHeight);
      };
      loadedImage.src = imageUrl;
    };

    load();
    const onResize = () => {
      if (!loadedImage || cancelled) return;
      computeBox(loadedImage.naturalWidth, loadedImage.naturalHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      loadedImage = null;
    };
  }, [imageUrl, open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-2000" role="dialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0 bg-black/40" onClick={close} aria-hidden />
      <div className="absolute left-1/2 top-1/2 w-[min(92vw,520px)] -translate-x-1/2 -translate-y-1/2 overflow-hidden bg-transparent">
        {imageUrl ? (
          <div
            className="relative mx-auto bg-transparent"
            style={
              imageBox
                ? { width: imageBox.width, height: imageBox.height }
                : { width: "min(92vw, 520px)", height: "min(80vh, 500px)" }
            }
          >
            <button
              type="button"
              onClick={close}
              className="absolute cursor-pointer right-2 top-2 z-10 grid h-9 w-9 place-items-center rounded-full bg-black/60 text-white hover:bg-black/70 sm:right-3 sm:top-3 sm:h-10 sm:w-10"
              aria-label="Close popup"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
            {href ? (
              <a href={href} onClick={close} className="absolute inset-0 block" target="_blank" rel="noopener noreferrer">
                <Image src={imageUrl} alt={title} fill className="object-contain rounded-3xl" unoptimized />
              </a>
            ) : (
              <Image src={imageUrl} alt={title} fill className="object-contain rounded-3xl" unoptimized />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
